import { ColorSwatch, Flex, Paper, Text, Title } from "@mantine/core";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { LikeResponse } from "../../../entities/components/LikeResponse";
import { useMediaQuery } from "@mantine/hooks";
import useReportStore from "../../../store/useReportStore";
import { PhasingOutSkill } from "../../../types/api";

interface Skill {
  name: string;
  color: string;
  description: string;
}

interface DataPoint {
  year: string;
  [key: string]: number | string;
}

const transformSkills = (
  phasingOutSkills: PhasingOutSkill[]
): { skills: Skill[]; data: DataPoint[] } => {
  const colors = ["#05D8D2", "#3A8BD9", "#6F40E2"];
  const skills: Skill[] = [];
  const skillNames: string[] = [];
  const data: DataPoint[] = [];

  phasingOutSkills?.forEach((skill, index) => {
    skills.push({
      name: skill.title,
      color: colors[index % colors.length],
      description: skill.description,
    });

    skillNames.push(skill.title);

    Object.keys(skill.phasing).forEach((year) => {
      if (!data.find((d) => d.year === year)) {
        data.push({ year });
      }
      const yearData = data.find((d) => d.year === year);
      if (yearData) {
        yearData[skill.title] = skill.phasing[year];
      }
    });
  });

  return { skills, data };
};

export const SkillsPhasingOut = () => {
  const matches = useMediaQuery("(max-width: 1100px)");

  const { report, reportId } = useReportStore();

  const { skills, data } = report?.skills_phasing_out
    ? transformSkills(report?.skills_phasing_out.data)
    : { skills: [], data: [] };

  const chartData = matches ? [data[0], data[1]] : data;

  const maxYAxisValue = Math.max(
    ...data.flatMap((d) =>
      Object.values(d).filter((v) => typeof v === "number")
    )
  );

  const yDomain = [0, maxYAxisValue + 100];

  if (!skills.length || !data.length) return null;

  return (
    <Paper radius="24px" p="32px" mt={32} withBorder>
      <Title size={matches ? 24 : 36} mb={8}>
        {report?.skills_phasing_out?.mark_down
          ? report?.skills_phasing_out?.mark_down.replace("##", "")
          : ""}
      </Title>
      <Text style={{ fontSize: matches ? 16 : 18 }} mb={24} c="dimmed">
        {report?.skills_phasing_out
          ? report?.skills_phasing_out?.explanation
          : " "}
      </Text>
      <ResponsiveContainer height={matches ? 466 : 647} width="100%">
        <BarChart data={chartData} margin={{ left: -8 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="year" />
          <YAxis
            domain={yDomain}
            ticks={[
              0,
              maxYAxisValue / 4,
              maxYAxisValue / 2,
              (maxYAxisValue * 3) / 4,
              maxYAxisValue + maxYAxisValue / 10,
            ]}
          />
          <Tooltip />
          {skills.map((skill, index) => (
            <Bar
              key={index}
              dataKey={skill.name}
              fill={skill.color}
              radius={[4, 4, 0, 0]}
            >
              <LabelList dataKey={skill.name} position="top" />
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>

      {skills.map((skill, index) => (
        <Flex key={index} gap={5} mt={16}>
          <ColorSwatch color={skill.color} size={14} radius={2} mt={5} />
          <Text size={matches ? "m" : "lg"}>
            <b>{skill.name}</b>: {skill.description}
          </Text>
        </Flex>
      ))}

      <LikeResponse report_id={reportId} section="skill_phasing_out" />
    </Paper>
  );
};
